"use client";

import Image from "next/image";
import { Button } from "@/components/ui/button";
import { Tag } from "@/components/tag";
import { CheckCircle2, XCircle, Minus, Eye, RotateCcw } from "lucide-react";
import type { DemoResult } from "@/types/demo";
import { DEMO_SECTIONS } from "@/app/coba/_components/demo-data";

/* Shared result content used by both desktop modal and mobile bottom sheet */
export function ResultContent({
  result,
  answers,
  tabSwitchCount,
  showReview,
  setShowReview,
  onRetry,
  onClose,
}: {
  result: DemoResult;
  answers: Record<string, string>;
  tabSwitchCount: number;
  showReview: boolean;
  setShowReview: (v: boolean) => void;
  onRetry: () => void;
  onClose: () => void;
}) {
  return (
    <>
      <div className="from-primary via-primary/90 to-primary text-primary-foreground relative bg-gradient-to-br px-5 pt-5 pb-5 sm:px-6">
        <div
          className="bg-card/[0.05] pointer-events-none absolute -top-8 -right-8 h-24 w-24 rounded-full"
          style={{ animation: "float-drift 15s ease-in-out infinite" }}
        />
        <p className="text-primary-foreground/35 text-xs font-black tracking-wider uppercase">
          Hasil Simulasi
        </p>
        <h2 className="text-primary-foreground mt-0.5 text-base font-black">
          Simulasi CPNS - SKD
        </h2>
        <div className="mt-4 flex items-end gap-2">
          <span
            className="text-5xl leading-none font-black"
            style={{
              textShadow:
                "0 3px 0 color-mix(in oklab, var(--foreground) 15%, transparent)",
            }}
          >
            {result.totalScore}
          </span>
          <span className="text-primary-foreground/35 mb-1 text-sm font-black">poin</span>
        </div>
        <div className="mt-3 flex flex-wrap gap-2 text-xs">
          <span className="bg-card/15 rounded-full border-2 border-white/10 px-2.5 py-1 font-black">
            <span className="text-green-300">{result.totalCorrect}</span> benar
          </span>
          <span className="bg-card/15 rounded-full border-2 border-white/10 px-2.5 py-1 font-black">
            <span className="text-red-300">{result.totalWrong}</span> salah
          </span>
          <span className="bg-card/15 rounded-full border-2 border-white/10 px-2.5 py-1 font-black">
            <span className="text-primary-foreground/50">{result.totalUnanswered}</span>{" "}
            kosong
          </span>
        </div>
        {tabSwitchCount > 0 && (
          <p className="mt-3 text-xs font-bold text-red-300/60">
            Pelanggaran: {tabSwitchCount} kali (-{tabSwitchCount * 10} detik)
          </p>
        )}
      </div>
      <div className="px-5 py-5">
        <div className="mb-4 flex justify-center">
          <span
            className={`rounded-full border-2 px-4 py-1.5 text-xs font-black ${result.isPassed ? "border-primary/20 bg-primary/20 text-primary" : "border-secondary/20 bg-secondary/50 text-secondary"}`}
          >
            {result.isPassed ? "SEMUA SEKSI LULUS" : "BELUM LULUS SEMUA SEKSI"}
          </span>
        </div>
        {result.sections.map((s) => (
          <div
            key={s.title}
            className={`mb-3 rounded-xl border-2 p-3.5 ${s.isPassed ? "border-primary/20 bg-primary/10" : "border-destructive/20 bg-destructive/10"}`}
          >
            <div className="flex items-center gap-2.5">
              <span
                className={`flex h-8 w-8 items-center justify-center rounded-xl text-sm font-black ${s.isPassed ? "bg-primary text-primary-foreground" : "bg-destructive text-primary-foreground"}`}
              >
                {s.score}
              </span>
              <div>
                <p className="text-foreground text-sm font-black">{s.title}</p>
                <p
                  className={`text-3xs font-black ${s.isPassed ? "text-primary" : "text-destructive"}`}
                >
                  {s.isPassed ? "LULUS" : "TIDAK LULUS"}
                </p>
              </div>
            </div>
            <div className="text-2xs mt-2 flex items-center gap-3 font-bold">
              <span className="text-primary flex items-center gap-1">
                <CheckCircle2 size={11} /> {s.correct} benar
              </span>
              <span className="text-destructive flex items-center gap-1">
                <XCircle size={11} /> {s.wrong} salah
              </span>
              <span className="text-foreground/60 flex items-center gap-1">
                <Minus size={11} /> {s.unanswered} kosong
              </span>
            </div>
          </div>
        ))}
        <Button
          type="button"
          variant="outline"
          size="lg"
          onClick={() => setShowReview(!showReview)}
          className="w-full"
        >
          <Eye size={12} className="mr-1.5 inline" />{" "}
          {showReview ? "Sembunyikan" : "Lihat"} Pembahasan
        </Button>
        {showReview && (
          <div className="mt-4 flex flex-col gap-4">
            {DEMO_SECTIONS.map((sec) => (
              <div key={sec.id}>
                <p className="text-foreground/30 mb-2.5 text-xs font-black tracking-wider uppercase">
                  {sec.title}
                </p>
                {sec.questions.map((q, qi) => {
                  const userAnswer = answers[q.id];
                  const correctOpt = q.options.find((o) => o.isCorrect);
                  const isCorrect = userAnswer === correctOpt?.key;
                  return (
                    <div
                      key={q.id}
                      className="border-border/60 shadow-soft-sm mb-3 overflow-hidden rounded-xl border-2"
                    >
                      <div className="flex items-start gap-2.5 px-3.5 pt-3.5 sm:px-4 sm:pt-4">
                        <span
                          className={`text-primary-foreground text-3xs mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-lg font-black ${isCorrect ? "bg-primary" : userAnswer ? "bg-destructive" : "bg-foreground/25"}`}
                        >
                          {qi + 1}
                        </span>
                        <div className="min-w-0 flex-1">
                          <p className="text-foreground text-xs leading-relaxed font-bold sm:text-sm">
                            {q.text}
                          </p>
                          {q.image && (
                            <Image
                              src={q.image}
                              alt="Soal"
                              width={300}
                              height={120}
                              sizes="(max-width: 768px) 100vw, 400px"
                              className="border-foreground/[0.08] bg-card mt-2 max-h-[120px] max-w-full rounded-lg border object-contain p-1.5"
                            />
                          )}
                          <Tag
                            tone={
                              isCorrect
                                ? "primary"
                                : userAnswer
                                  ? "destructive"
                                  : "neutral"
                            }
                            size="sm"
                            className="mt-1 font-black"
                          >
                            {isCorrect ? "Benar" : userAnswer ? "Salah" : "Tidak Dijawab"}
                          </Tag>
                        </div>
                      </div>
                      <div className="mt-2.5 flex flex-col gap-1 px-3.5 sm:px-4">
                        {q.options.map((opt) => {
                          const isUserPick = userAnswer === opt.key;
                          const isRight = opt.isCorrect;
                          return (
                            <div
                              key={opt.key}
                              className={`flex items-center gap-2 rounded-lg px-2.5 py-1.5 text-xs ${isRight ? "bg-primary/30 text-primary font-bold" : isUserPick && !isRight ? "bg-destructive/20 text-destructive font-bold" : "text-foreground/50"}`}
                            >
                              <span
                                className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[9px] font-black ${isRight ? "bg-primary text-primary-foreground" : isUserPick ? "bg-destructive text-primary-foreground" : "bg-foreground/[0.06] text-foreground/60"}`}
                              >
                                {opt.key}
                              </span>
                              <div className="flex flex-1 items-center gap-2">
                                {opt.image && (
                                  <Image
                                    src={opt.image}
                                    alt={opt.key}
                                    width={40}
                                    height={40}
                                    className="border-foreground/[0.08] bg-card h-10 w-10 rounded border object-contain p-0.5"
                                  />
                                )}
                                {opt.text && <span>{opt.text}</span>}
                              </div>
                              {isUserPick && !isRight && (
                                <span className="text-destructive shrink-0 text-[9px]">
                                  Jawabanmu
                                </span>
                              )}
                              {isRight && (
                                <span className="text-primary shrink-0 text-[9px]">
                                  Jawaban Benar
                                </span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                      <div className="border-border/60 bg-primary/20 mt-3 border-t-2 border-dashed px-3.5 py-3 sm:px-4">
                        <p className="text-primary/60 text-3xs mb-1 font-black tracking-wider uppercase">
                          Pembahasan
                        </p>
                        <p className="text-foreground/60 text-xs leading-relaxed sm:text-[13px]">
                          {q.explanation}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        )}
        <div className="mt-4 flex gap-2">
          <Button
            type="button"
            variant="outline"
            size="xl"
            onClick={onRetry}
            className="flex-1"
          >
            <RotateCcw size={14} /> Coba Lagi
          </Button>
          <Button type="button" size="xl" onClick={onClose} className="flex-1">
            Tutup
          </Button>
        </div>
      </div>
    </>
  );
}
